import { AccountSelector } from '@/design-system/components/accountSelector/AccountSelector';
import { AlertType, UniAlert } from '@/design-system/components/alerts';
import { UniButton } from '@/design-system/components/buttons';
import { Divider } from '@/design-system/components/Divider';
import { UniTabHeader } from '@/design-system/components/tabs';
import { H6, TextBody, TextCaption, TextTitle } from '@/design-system/components/typography';
import { formatAmountByDecimals } from '@/utils/numberFormatting';
import { useKeyboard } from '@react-native-community/hooks';
import { useNavigation } from '@react-navigation/native';
import { ScrollView, SxProp, View } from 'dripsy';
import _ from 'lodash';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Platform } from 'react-native';
import { RootStackRoutes } from 'types';
import { GetBrokersResponseItem } from '@utx/utx-client';
import ExchangeBanner from '../../../../assets/svg/ExchangeBanner.svg';
import { ErrorCode } from '../../enum';
import { ExchangeSubAccountStackRoutes } from '../../exchange.navigator';
import { useExchangeSubAccount } from '../../hooks/useExchangeSubAccount.hook';
import { useGetCalcFee } from '../../hooks/useGetCalcFee.hook';
import { useNavigatorPage } from '../../hooks/useNavigatorPage.hook';
import { ScaledImage } from '../components/ScaledImage';
export interface ConfirmExchangeProps {
  data: GetBrokersResponseItem;
  shouldBeFetchFee: boolean;
  onClose: () => void;
}
export const ConfirmExchangeScreen: React.FC<ConfirmExchangeProps> = (props) => {
  const { t } = useTranslation('exchange-sub-account');
  const navigation = useNavigation();
  const exchangeSubAccount = useExchangeSubAccount();
  const calcFee = useGetCalcFee({ enabledFetchMonthlyFee: false, enabledFetchAppFee: false });
  const keyboard = useKeyboard();
  const navigator = useNavigatorPage();
  const data = props.data;
  const shouldBeFetchFee = props.shouldBeFetchFee;
  const {
    monthlyData: { fee: monthlyFee },
    applicationData: { fee: applicationFee }
  } = calcFee;
  const {
    general: {
      wallets,
      walletId,
      walletLabel,
      alertMessage,
      balance,
      assetName,
      setAlertMessage,
      setWalletId,
      setAssetName,
      getAccountType
    },
    postOpenAccount: { isLoading: loadingOpenAccount }
  } = exchangeSubAccount;
  const { userFee: applicationUserFee, feeCurrency: applicationFeeCurrency } = applicationFee || {};
  const { userFee: monthlyUserFee, feeCurrency: monthlyFeeCurrency } = monthlyFee || {};
  const { feeSummary } = data.metadata || {};
  const { estimatedAccountOpeningTime } = feeSummary || {};

  useEffect(() => {
    onUpdateAssetName();
    onRefetchFee();
  }, []);

  useEffect(() => {
    if (Platform.OS === 'web') {
      const popStateListener = (event: any) => {
        props.onClose();
      };
      window.addEventListener('popstate', popStateListener, false);

      return () => window.removeEventListener('popstate', popStateListener);
    }
  }, []);

  useEffect(() => {
    onUpdateAssetName();
  }, [applicationFeeCurrency]);

  const onRefetchFee = () => {
    if (shouldBeFetchFee) {
      calcFee.applicationData.refetch();
      calcFee.monthlyData.refetch();
    }
  };

  const onUpdateAssetName = () => {
    if (!_.isEmpty(applicationFeeCurrency)) {
      setAssetName(applicationFeeCurrency);
    }
  };

  const displayAmountWithCurrency = (amountString: string, currency: string) => {
    const amount = parseFloat(amountString);
    return `${amount < 0 ? '-' : ''}${formatAmountByDecimals({ value: Math.abs(amount), decimals: 2 })} ${currency ?? 'HKD'}`;
  };

  const onConfirm = () => {
    setAlertMessage('');
    exchangeSubAccount.postOpenAccount.postOpenAccountApi(
      {
        brokerId: data.id,
        walletId: walletId
      },
      (data) => {
        setTimeout(() => {
          navigator.replaceScreen(RootStackRoutes.ExchangeSubAccount, ExchangeSubAccountStackRoutes.InstructionSubmitted);
        }, 350);
      },
      (error) => {
        var statusCode: number | undefined = error.response?.status;
        if (statusCode === ErrorCode.EXPIRED_TOKEN_ERROR) {
        } else {
          setAlertMessage(error.response?.data?.message ?? '');
        }
      }
    );
  };

  const applicationFeeAmount = applicationFee?.userFee ?? '0';

  const balanceAmount = parseFloat(balance);
  const debitAmount = parseFloat(applicationFeeAmount);
  const getIcon = (logoUrl: string) => {
    return (
      <View sx={sxStyles.iconWrapper}>
        <ScaledImage uri={logoUrl ?? ''} />
      </View>
    );
  };

  return (
    <View sx={sxStyles.container}>
      <UniTabHeader title={t('exchange-account::glossary.application-instruction')} onBack={props.onClose} />
      <ScrollView>
        <View sx={sxStyles.content}>
          <View sx={sxStyles.body}>
            <TextTitle semiBold>{t('exchange-account::confirmation.text--you-have-selected-account')}</TextTitle>
            <View sx={sxStyles.viewBinance}>
              <View sx={sxStyles.viewBinanceBackground}>
                <ExchangeBanner width={'100%'} height={'100%'} preserveAspectRatio={'none'} />
              </View>
              <View sx={sxStyles.viewBinanceWrapper}>
                <H6 bold sx={sxStyles.textBinance}>
                  {data.name}
                </H6>
                {getIcon(data.logoUrl)}
              </View>
            </View>

            <View sx={{ zIndex: 1000, mb: -10 }}>
              <AccountSelector asset={assetName} onValueChange={setWalletId} value={walletId} />
            </View>

            <View
              sx={{
                ...sxStyles.horizontalWrapper,
                ...{ mb: '$5' }
              }}>
              <TextCaption sx={sxStyles.textLabel}>{t('exchange-account::glossary.balance')}</TextCaption>
              <TextCaption>{displayAmountWithCurrency(balance, assetName)}</TextCaption>
            </View>
            <View
              sx={{
                ...sxStyles.horizontalWrapper,
                ...{ mb: '$3' }
              }}>
              <TextCaption sx={sxStyles.textLabel}>{t('exchange-account::glossary.account-application-fee')}</TextCaption>
              <TextCaption sx={{ color: '$danger' }}>- {displayAmountWithCurrency(applicationUserFee ?? '0', assetName)}</TextCaption>
            </View>
            <View
              sx={{
                ...sxStyles.horizontalWrapper,
                ...{ mb: '$3' }
              }}>
              <TextCaption sx={sxStyles.textLabel}>{t('exchange-account::glossary.monthly-fee')}</TextCaption>
              <TextCaption>
                {displayAmountWithCurrency(monthlyUserFee ?? '0', !_.isEmpty(monthlyFeeCurrency) ? monthlyFeeCurrency : assetName)}
              </TextCaption>
            </View>
            <View
              sx={{
                ...sxStyles.horizontalWrapper,
                ...{ mb: '$10' }
              }}>
              <TextCaption sx={sxStyles.textLabel}>{t('exchange-account::glossary.est-account-opening-time')}</TextCaption>
              <TextCaption>{estimatedAccountOpeningTime ?? '-'}</TextCaption>
            </View>
            <Divider />
            <TextCaption semiBold sx={sxStyles.textDebitAmount}>
              {t('exchange-account::glossary.debit-amount')}
            </TextCaption>
            <H6 bold sx={sxStyles.textValueDebitAmount}>
              - {displayAmountWithCurrency(`${debitAmount}`, assetName)}
            </H6>
            {!_.isEmpty(walletId) && (
              <TextBody>{t('exchange-account::glossary.will-be-deducted', { wallet: getAccountType(walletLabel) })}</TextBody>
            )}
            {!_.isEmpty(alertMessage) && <UniAlert type={AlertType.Error} message={alertMessage} sx={{ mt: 5 }} />}
            {(balanceAmount < debitAmount || wallets.length == 0) && !_.isEmpty(walletId) && (
              <TextBody sx={sxStyles.textInsufficient}>{t('exchange-account::account-selection.text--insufficient-balance')}</TextBody>
            )}
          </View>
        </View>
      </ScrollView>
      <View sx={sxStyles.buttonView}>
        <UniButton
          label={t('general::confirm')}
          onPress={onConfirm}
          sx={sxStyles.button}
          size="medium"
          disabled={balanceAmount < debitAmount || wallets.length == 0 || _.isEmpty(walletId)}
          loading={loadingOpenAccount}
        />
      </View>
    </View>
  );
};

const sxStyles: Record<string, SxProp> = {
  container: {
    flex: 1,
    minHeight: 700,
    backgroundColor: '$backgroundPrimary'
  },
  body: {
    flexDirection: 'column',
    flex: 1
  },
  content: {
    mx: '$4',
    mt: '$5',
    pb: '$$25'
  },
  viewBinance: {
    width: '100%',
    height: 70,
    gap: 20,
    borderRadius: '$radius2',
    my: '$5',
    overflow: 'hidden'
  },
  viewFilterDropdown: {
    width: '100%',
    borderRadius: '$radius1',
    borderColor: '#CCCCCC',
    borderWidth: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    px: '$4',
    my: '$1',
    height: 48,
    zIndex: 1000
  },
  horizontalWrapper: {
    flexDirection: 'row',
    justifyContent: 'space-between'
  },
  textLabel: {
    color: '$textSecondary'
  },
  textDebitAmount: {
    mt: '$5',
    mb: '$1',
    color: '$textPrimary'
  },
  textValueDebitAmount: {
    mb: '$1',
    color: '$danger'
  },
  buttonView: {
    position: 'absolute',
    left: 16,
    right: 16,
    bottom: 25
  },
  button: {
    height: 44
  },
  viewBinanceBackground: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    bottom: 0
  },
  viewBinanceWrapper: {
    flexDirection: 'row',
    width: '100%',
    height: '100%',
    justifyContent: 'space-between',
    alignItems: 'center',
    px: '$5'
  },
  textBinance: {
    color: '$textDarkBackground'
  },
  viewDropdownAbsolute: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    px: '$4',
    height: '$11'
  },
  iconWrapper: {
    width: '50%',
    height: '100%',
    alignItems: 'flex-end',
    justifyContent: 'center'
  },
  textInsufficient: {
    color: '$danger',
    mt: '$3'
  }
};
